import { DateTime } from 'luxon';
import { OnAccount } from './on-account/on-account';
import { OrderPaymentMethod } from '../order/order';
import { StripeBankAccount } from './payment-v2';
import Dinero from 'dinero.js';
import { PaymentIntent, StripeError } from '@stripe/stripe-js';
import { PaginationRequest } from '../pagination/pagination';

export enum PaymentMethodCode {
  Stripe = 'stripe',
}

export class PaymentMethodField {
  id!: number;
  code!: PaymentMethodCode;
  key!: string;
  value!: string;
}

export class StripeShippingAddress {
  line1!: string;
  line2?: string;
  city!: string;
  state!: string;
  country!: string;
  postalCode!: string;
}

export class CardRequest {
  paymentMethod!: string;
  cardholderName!: string;
  nickname?: string;
}

export class AdminCardRequest extends CardRequest {
  userId!: number;
  zip?: string;
  expirationMonth?: string;
  expirationYear?: string;
  setupIntentId?: string;
  confirmationTokenId?: string;
}

export class StripeCard {
  id!: number;
  userId!: number;
  stripeCardId!: string;
  cardholderName!: string;
  brand!: string;
  expirationMonth!: string;
  expirationYear!: string;
  last4!: string;
  nickname?: string;
  cardType?: StripeCardType;
  zip?: string;
  createdAt!: DateTime;
  updatedAt!: DateTime;
  deletedAt?: DateTime;
  lockTimestamp?: DateTime;
  confirmationTokenId?: string;
  setupIntentId?: string;
}

export enum StripeCardType {
  credit = 'credit',
  debit = 'debit',
  prepaid = 'prepaid',
  unknown = 'unknown',
}

export class StripeConfig {
  publishableKey!: string;
  connectedAccountId!: string;
  transactionMode!: StripeTransactionMode;
  accountType!: string;
}

export enum StripeTransactionMode {
  'Test' = 'Test',
  'Live' = 'Live',
}

export enum RefundReason {
  Duplicate = 'Duplicate',
  Fraudulent = 'Fraudulent',
  RequestedByCustomer = 'Requested by customer',
  OrderCancelled = 'Order cancelled',
  Other = 'Other',
}

export class CreateRefundRequest {
  paymentIdToRefund!: number;
  partialAmount!: number;
  reason!: RefundReason;
  comments!: string;
}

export class ChargeHoldRequest {
  paymentId!: number;
  amount!: number;
  comments!: string;
}

export class OrderPaymentStatus {
  status!: boolean;
  message?: string;
  payment?: OrderPayment;
}

export enum PaymentReason {
  Refund = 'Refund',
  SecurityDeposit = 'Security Deposit',
  OrderPayment = 'Order Payment',
  Extension = 'Extension',
  Upcharge = 'Upcharge',
}

export enum PaymentStatus {
  pending = 'pending',
  succeeded = 'succeeded',
  failed = 'failed',
}

export enum PaymentMethodType {
  Stripe = 'Stripe',
  Cash = 'Cash',
  Check = 'Check',
  OnAccount = 'On Account',
  Other = 'Other',
  Statement = 'Statement',
  None = 'None',
  Custom = 'Custom',
  Credit = 'Credit',
}

export enum OrderPaymentType {
  Refund = 'Refund',
  OrderPayment = 'Order Payment',
  UpchargePayment = 'Upcharge Payment',
  Uncaptured = 'Uncaptured',
  Captured = 'Captured',
}

export class StripePaymentMethodRequest {
  type!: PaymentMethodType.Stripe;
  cardId?: number;
  bankAccountId?: string;
  method?: string;
  poNumber?: string;
  terminalId?: string;
  confirmationTokenId?: string;
  bankCard?: boolean;
}

export class StripePaymentMethod {
  type!: PaymentMethodType.Stripe;
  card?: StripeCard;
  bankAccount?: StripeBankAccount;
  method?: string;
  paymentIntentId?: string;
}

export class CashPaymentMethod {
  type!: PaymentMethodType.Cash;
  comments?: string;
  poNumber?: string;
}

export class CheckPaymentMethod {
  type!: PaymentMethodType.Check;
  checkNumber?: number;
  poNumber?: string;
}

export class CustomPaymentMethod {
  type!: PaymentMethodType.Custom;
  name!: string;
  memo?: string;
  poNumber?: string;
}

export class CreditPaymentMethod {
  type!: PaymentMethodType.Credit;
  paymentId?: string;
  comments?: string;
}
export class CreditPaymentMethodRequest {
  type!: PaymentMethodType.Credit;
  paymentId!: string;
  comments?: string;
}

export class OnAccountPaymentMethodRequest {
  type!: PaymentMethodType.OnAccount;
  accountNumber!: string;
  poNumber?: string;
}

export class OnAccountPaymentMethod {
  type!: PaymentMethodType.OnAccount;
  account!: OnAccount;
  poNumber?: string;
}

export class OtherPaymentMethod {
  type!: PaymentMethodType.Other;
  comments?: string;
  poNumber?: string;
}

export class NonePaymentMethod {
  type!: PaymentMethodType.None;
  poNumber?: string;
}

export class StatementPaymentMethod {
  type!: PaymentMethodType.Statement;
  paymentId?: string;
  poNumber?: string;
}

export type PaymentMethodRequest =
  | StripePaymentMethodRequest
  | CashPaymentMethod
  | CheckPaymentMethod
  | OnAccountPaymentMethodRequest
  | CustomPaymentMethod
  | CreditPaymentMethod
  | NonePaymentMethod;

export type PaymentMethod =
  | StripePaymentMethod
  | CashPaymentMethod
  | CheckPaymentMethod
  | OnAccountPaymentMethod
  | CustomPaymentMethod
  | CreditPaymentMethod
  | NonePaymentMethod;

export const toOrderPaymentMethod = (paymentMethod?: PaymentMethod): OrderPaymentMethod | undefined => {
  if (!paymentMethod) {
    return undefined;
  }

  switch (paymentMethod.type) {
    case PaymentMethodType.Stripe: {
      if (paymentMethod.card) {
        return OrderPaymentMethod.CreditCard;
      }
      return OrderPaymentMethod.BankAccount;
    }
    case PaymentMethodType.Cash:
      return OrderPaymentMethod.Cash;
    case PaymentMethodType.Check:
      return OrderPaymentMethod.Check;
    case PaymentMethodType.OnAccount:
      return OrderPaymentMethod.OnAccount;
    case PaymentMethodType.Credit:
      return OrderPaymentMethod.Credit;
    case PaymentMethodType.None:
    default:
      return OrderPaymentMethod.ProceedWithoutPayment;
  }
};

export class OrderPayment {
  id!: number;
  orderId!: number;
  paymentMethod!: string;
  gatewayTransactionId!: string;
  amount!: number;
  balance!: number;
  gatewayStatus!: string;
  comments!: string;
  gatewayResponse!: string;
  paymentType!: OrderPaymentType;
  date!: DateTime;
  status!: PaymentStatus;
  reason!: PaymentReason;
  method!: PaymentMethodType;
  methodDetail?: string;
  refunded?: boolean;
  refundedPaymentId?: number;
  refunds!: OrderPayment[];
  addedBy?: number;
  taxCharged?: number;
}

export class StripeTerminal {
  id!: string;
  label!: string;
  status!: string;
}

export class TerminalLocation {
  id!: string;
  locationId!: number;
  stripeLocationId!: string;
  createdAt!: DateTime;
  deletedAt?: DateTime;
}

export class Terminal {
  id!: string;
  terminalLocationId!: string;
  serial!: string;
  stripeTerminalId!: string;
  label!: string;
  status!: string;
  model!: string;
  createdAt!: DateTime;
  deletedAt?: DateTime;
}

export enum UpchargeReason {
  LateCharge = 'Late Charge',
  DamagedProducts = 'Damaged Product(s)',
  MissingProducts = 'Missing Product(s)',
  FuelCharge = 'Fuel Charge',
  Other = 'Other',
}

export interface InvoicePayment {
  id: string;
  invoiceId: string;
  paymentId: string;
  amount: number;
  updatedAt?: DateTime;
  createdAt?: DateTime;
  deletedAt?: DateTime;
}

export type Payment = {
  id: string;
  invoiceId: string;
  method: string;
  methodDetail?: string;
  status: PaymentStatus;
  gatewayId?: string;
  gatewayStatus?: string;
  gatewayResponse?: string;
  amount: number;
  refundedPaymentId?: string;
  userId: number;
  invoicePayments: InvoicePayment[];
  paidAt: DateTime;
  createdBy?: number;
  processingFee?: number;
  availableAmount?: number;
};

export enum PaymentRefundRequirement {
  InPerson = 'InPerson',
  Canceled = 'Canceled',
}

export type PaymentDetail = {
  intentId: string;
  type: 'CardPresent' | 'CardNotPresent' | 'Electronic';
  amount: Dinero.Dinero;
};

export class StripePayment {
  paymentIntent!: PaymentIntent;
  card?: StripeCard;
  error?: StripeError;
  amount!: number;
  adjustment?: number;
  customerFee?: { fixed?: number; percent?: number; percentAmount?: number };
}

export type SearchPaymentRequest = {
  ids?: string[];
  refundedPaymentIds?: string[];
  from?: DateTime;
  to?: DateTime;
  methods?: PaymentMethodType[];
  amountGreaterThan?: number;
  amountLessThan?: number;
  userIds?: number[];
} & PaginationRequest;

export type CheckedPaymentResponse = {
  pending: Payment[];
  suspicious: Payment[];
  corrected?: Payment[];
};

export const DUBIOUS_GATEWAY_STATUSES = [
  'fraudulent',
  'pickup_card',
  'lost_card',
  'stolen_card',
  'restricted_card',
  'merchant_blocklist',
];
